import { useState, useEffect } from 'react';

/**
 * Hook to check if an image URL loads successfully
 * Returns true only if the image loads, false otherwise
 */
export function useImageLoader(url: string | null | undefined): boolean {
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (!url) {
      setIsValid(false);
      return;
    }

    const img = new Image();
    
    img.onload = () => {
      setIsValid(true);
    };
    
    img.onerror = () => {
      setIsValid(false);
    };

    img.src = url;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [url]);

  return isValid;
}

/**
 * Hook to check multiple image URLs and return which ones are valid
 */
export function useMultipleImageLoader(urls: (string | null | undefined)[]): boolean[] {
  const [validStates, setValidStates] = useState<boolean[]>(() => urls.map(() => false));

  useEffect(() => {
    const newStates = urls.map(() => false);
    let mounted = true;

    urls.forEach((url, index) => {
      if (!url) {
        return;
      }

      const img = new Image();
      
      img.onload = () => {
        if (mounted) {
          setValidStates(prev => {
            const updated = [...prev];
            updated[index] = true;
            return updated;
          });
        }
      };
      
      img.onerror = () => {
        if (mounted) {
          setValidStates(prev => {
            const updated = [...prev];
            updated[index] = false;
            return updated;
          });
        }
      };

      img.src = url;
    });

    return () => {
      mounted = false;
    };
  }, [urls.join(',')]);

  return validStates;
}
